import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { SsdParserService, engines } from './ssd-parser.service';
import { SessionService } from './session.service';
import { TableService } from './table.service';

import { Ship } from '../interfaces/ship';
import { Race } from '../interfaces/race';

@Injectable({
  providedIn: 'root'
})
export class ShipService {
  private http = inject(HttpClient);
  private session = inject(SessionService);
  private ssdParserService = inject(SsdParserService);
  private tableService = inject(TableService);

  hullClassByClassName: { [key: string]: any; } = {};
  countedByClassId: { [key: string]: any; } = {};

  constructor () {
    this.tableService.getTableDataForType('hullClasses', this.session.campaign._id).subscribe((hullClasses) => {
      let emptyHash: { [key: string]: any; } = {};
      this.hullClassByClassName = hullClasses.reduce(
        (hash, hullClass) => {
          let className = hullClass['className'];
          hash[className] = hullClass;
          return hash;
        },
        emptyHash
      );
    });
  };

  getShipForId$ (shipId: string): Observable<Ship> {
    return this.http.get<Ship>('/api/ship/id/' + shipId);
  };

  getShipsForRaceId$ (raceId: string): Observable<Ship[]> {
    return this.http.get<Ship[]>('/api/ship/race/' + raceId);
  };

  getShipYardsForRace$ (race: Race): Observable<Ship[]> {
    return this.http.get<Ship[]>('/api/ship/sy/race/' + race._id);
  };

  saveShip$ (ship: Ship): Observable<Ship> {
    if (ship._id != null) {
      return this.http.put<Ship>('/api/ship/' + ship._id, ship);
    }
    else {
      return this.saveNewShip$(ship);
    }
  };

  saveNewShip$ (ship: Ship): Observable<Ship> {
    return this.http.post<Ship>('/api/ship', ship);
  };

  saveNewShips$ (ships: Ship[]): Observable<Ship[]> {
    return this.http.post<Ship[]>('/api/ships', ships);
  };

  deleteShipForId$ (shipId: string) {
    return this.http.delete('/api/ship/id/' + shipId);
  };

  countItems (ship: Ship, countedItems = []) {
    let countingEngines = false;
    let itemsToCount = [...countedItems];
    if (itemsToCount.length > 0) {
      let index = itemsToCount.indexOf('engines');
      if (index > -1) {
        itemsToCount.splice(index, 1, ...engines);
        countingEngines = true;
      }
    }
    let removeRequiredQH = false;
    let counted = {};
    for (const key of itemsToCount) {
      counted[key] = 0;
      removeRequiredQH = removeRequiredQH || (key === 'Q' || key === 'H');
    }
    let items = this.ssdParserService.parseSsd(ship.ssd);
    for (const item of items) {
      if (itemsToCount.indexOf(item) > -1) {
        counted[item] = (counted[item] || 0) + 1;
      }
    }
    if (removeRequiredQH) {
      if (ship.actualSize > 7) {
        if (counted['Hs'] > 0) {
          counted['Hs'] = counted['Hs'] - 1;
        }
        else {
          counted['H'] = counted['H'] - 1;
        }
      }
      let qPoints = ship.actualSize;
      while (qPoints > 0 && counted['Qs'] > 0) {
        qPoints = qPoints - 20;
        counted['Qs'] = counted['Qs'] - 1;
      }
      while (qPoints > 0 && counted['Q'] > 0) {
        qPoints = qPoints - 50;
        counted['Q'] = counted['Q'] - 1;
      }
    }
    if (countingEngines) {
      let militaryEngines = counted['I'] + counted['i'] / 2;
      let commercialEngines = counted['Ic'] + counted['ic'] / 2;
      let hullClass = this.hullClassByClassName[ship.hullClass.toUpperCase()];
      let enginePower = hullClass['enginePower'];
      let militarySpeed = Math.min(Math.round(militaryEngines / enginePower), hullClass['maxI']);
      let commercialSpeed = Math.min(Math.round(commercialEngines / enginePower), hullClass['maxIc']);
      counted['speed'] = Math.max(militarySpeed, commercialSpeed);
    }
    return counted;
  };
};
